import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Btn from 'components/buttons/Btn'
import Reloader from 'components/buttons/Reloader'
import { FixedWarning } from 'components/FixedWarnings/FixedWarning'
import Screen from 'components/layout/Screen'
import { capitalize } from 'lodash-es'
import { Badge, Nav } from 'react-bootstrap'
import { Trans, useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router'
import { Link, NavLink, useParams } from 'react-router-dom'
import API from 'state/API'
import Order from 'state/models/Order'
import { fetchOrders, getOrdersCount } from 'state/Orders'
import { refreshRestaurants, useActiveRestaurant, useRestaurants } from 'state/Restaurants'
import { useObjSelector } from 'utils'
import DateSelector from './DateSelector'
import Footer from './Footer'
import OrdersList from './OrdersList'
import './styles.scss'

/**
 * List of order statuses used in the URL, pipe-separated; useful for regex checks.
 * @type {string}
 */
OrdersPage.URIS = Object.values(Order.STATUS).join('|')

OrdersPage.useMapStates = (iterator) => {
	const { status }  = useParams()
	const ordersCount = useObjSelector(getOrdersCount)
	return Object.entries(Order.STATUS).map(([key, slug]) => {
		const link      = `/${slug}`
		const icon      = Order.ICONS[slug]
		const text      = Order.STATUS_TXT[key]
		const isThisOne = (slug == status)
		const count     = ordersCount[key]
		return iterator(key, link, icon, text, isThisOne, count)
	})
}

export const cornerButtons = {
	search  : t => <Btn key="search" to="/search" as={Link} icon="search" fixedWidth title={t('Search orders')}/>,
	reloader: (doReload, hideXs = true) => (
		<Reloader key="reloader" resource={API.RESOURCES.ORDERS} reloader={doReload} className={hideXs? 'd-none d-sm-inline' : ''}/>
	)
}

//Note: this component re-renders every time the tab changes (AKA URL). This
// makes memoization of order filters harder, as everything inside here is going
// to re-run. A way to improve performance on that side is having all OrdersList
// components rendering together, and using the tabs to simply switch between
// them... but then we lose the URL (unless we find a way to change URL skipping
// re-render?), and exchange memoization for extra components pre-rendered.
// Maybe we could also change the routing and create a sub-router here? So the
// tabs stay the same, while the list changes.
// What's better? ... Does it even matter?
export default function OrdersPage() {
	const { t }      = useTranslation()
	const { status } = useParams()
	const dispatch   = useDispatch()
	const countRest  = useRestaurants().length
	const restaurant = useActiveRestaurant()?.display_name //won't be available if "all restaurants" is selected
	const isFuturePg = useLocation().pathname == '/future'

	function reload() {
		return Promise.all([
			dispatch(refreshRestaurants),
			dispatch(fetchOrders)
		])
	}

	const titleStatus = capitalize(t(status, { context: 'orders' })) //same as in the menu, no need for commented t()
	const forLoc      = countRest > 1 && restaurant? t('for {{restaurant}}', { restaurant }) : ''
	const corner      = [
		!isFuturePg && <DateSelector key="dates"/>,
		cornerButtons.search(t),
		cornerButtons.reloader(reload),
	]
	return (
		<Screen title={restaurant || t('{{status}} Orders', { status: titleStatus })} corner={corner} forcedTitle
				pageTitle={forLoc?
					t('{{status}} Orders {{forLoc}}', { status: titleStatus, forLoc }) :
					t('{{status}} Orders', { status: titleStatus }) //Chinese needs this differentiation
				}
		>
			<Nav fill as="nav" variant="tabs" role="tabpanel" className="d-sm-none">
				{OrdersPage.useMapStates((key, link, icon, text, isCurrent, count) => (
					<Nav.Link className="nav-item" as={NavLink} key={key} to={link}>
						<FontAwesomeIcon {...icon}/>{' '}
						<span>{text}</span>{' '}
						<Badge variant={isCurrent? 'primary' : 'light'}>{count}</Badge>
					</Nav.Link>
				))}
				<Reloader nav resource={API.RESOURCES.ORDERS} reloader={reload}/>
			</Nav>

			<Screen.Content>
				<FixedWarning variant="warning" {...Order.ICONS[Order.STATUS.FUTURE]} show={isFuturePg}
				              btnTxt={t('See current orders')} action="/open">
					<Trans>These are <b>FUTURE orders</b>, not due soon.</Trans>
				</FixedWarning>

				<OrdersList isFuture={isFuturePg}/>
				<Footer isFuture={isFuturePg}/>
			</Screen.Content>
		</Screen>
	)
}
